// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-COST-6 — dialogs for manual / recurring lines: costs (overhead), and other income / credits (revenue).
// Add/edit, change one month, cut off.

import { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import AppDialog from '../../../components/ui/AppDialog'
import AppButton from '../../../components/ui/AppButton'
import Notice from '../../../components/ui/Notice'
import { AppTextField, ChoiceToggle, MoneyField, SelectField, SuggestField } from '../../../components/ui/FormFields'
import type { CostCadence, CostItem, CostItemInput, CostKind } from '../../../api/finance'

export const CATEGORIES = ['GitHub seats', 'AI tooling', 'SaaS tool', 'Domain / DNS', 'Courier fees', 'Other']
export const INCOME_CATEGORIES = ['Setup / onboarding fee', 'Implementation', 'Extra billing', 'Training', 'Discount', 'Refund', 'Goodwill credit', 'Other']

/** Which side of the P&L a dialog writes: costs (expenses) or income + credits (revenue). */
export type LineMode = 'cost' | 'income'
export const KIND_LABEL: Record<CostKind, string> = { cost: 'Cost', income: 'Income', credit: 'Credit' }

export const CADENCE_LABEL: Record<CostCadence, string> = {
  one_off: 'One-off', monthly: 'Monthly', annual_spread: 'Yearly (÷12)',
}
const CADENCES = (Object.keys(CADENCE_LABEL) as CostCadence[]).map(c => ({ value: c, label: CADENCE_LABEL[c] }))

export function monthLabel(m: string) {
  const [y, mo] = m.split('-').map(Number)
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/** Jan 2025 → 12 months ahead of today, newest first. A fixed, bounded list (no free-typed dates). */
export function monthOptions(): string[] {
  const out: string[] = []
  const now = new Date()
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 12, 1))
  while (d.getUTCFullYear() >= 2025) {
    out.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`)
    d.setUTCMonth(d.getUTCMonth() - 1)
  }
  return out
}

const asOptions = (ms: string[]) => ms.map(m => ({ value: m, label: monthLabel(m) }))
const money = (n: number) => `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const parseMoney = (s: string) => (s.trim() === '' ? NaN : Number(s))
const moneyOk = (n: number) => Number.isFinite(n) && n >= 0 && n <= 1_000_000

// ── add / edit ─────────────────────────────────────────────────────────────────────────────────────

export function CostItemDialog({ open, initial, defaultMonth, busy, error, onSave, onClose, mode = 'cost', customers = [] }: {
  open: boolean
  /** null = add a new line */
  initial: CostItem | null
  defaultMonth: string
  busy: boolean
  error: string | null
  onSave: (input: CostItemInput) => void
  onClose: () => void
  mode?: LineMode
  /** income/credit may be tied to one customer */
  customers?: { id: string; name: string }[]
}) {
  const months = useMemo(monthOptions, [])
  const [f, setF] = useState<CostItemInput>(blank(defaultMonth, mode))
  const [amount, setAmount] = useState('')
  useEffect(() => {
    if (!open) return
    setF(initial ? { ...initial } : blank(defaultMonth, mode))
    setAmount(initial ? String(initial.amount_usd) : '')
  }, [open, initial, defaultMonth, mode])
  const noun = mode === 'cost' ? 'cost' : f.kind === 'credit' ? 'credit' : 'income'

  const amt = parseMoney(amount)
  const endOk = f.cadence === 'one_off' || f.end_month === null || f.end_month >= f.start_month
  const valid = f.title.trim() !== '' && f.category.trim() !== '' && moneyOk(amt) && endOk
  const save = () => onSave({ ...f, amount_usd: Math.round(amt * 100) / 100, end_month: f.cadence === 'one_off' ? null : f.end_month })

  return (
    <AppDialog open={open} onClose={onClose} busy={busy} title={initial ? `Edit ${noun}` : mode === 'cost' ? 'Add a cost' : 'Add income or a credit'}
      actions={<>
        <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
        <AppButton variant="contained" onClick={save} disabled={!valid || busy}>{busy ? 'Saving…' : 'Save'}</AppButton>
      </>}>
      <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
        {error && <Notice severity="error">{error}</Notice>}
        {mode === 'income' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr' }, gap: 2, alignItems: 'center' }}>
            <ChoiceToggle ariaLabel="Income or credit" value={f.kind === 'credit' ? 'credit' : 'income'}
              options={[{ value: 'income', label: 'Income (+)' }, { value: 'credit', label: 'Credit / discount (−)' }]}
              onChange={v => setF({ ...f, kind: v })} />
            <SelectField label="Customer" value={f.pharmacy_id ?? ''} onChange={v => setF({ ...f, pharmacy_id: v || null })}
              options={[{ value: '', label: 'Not tied to one customer' }, ...customers.map(c => ({ value: c.id, label: c.name }))]} />
          </Box>
        )}
        <AppTextField label="Name" required value={f.title} onChange={e => setF({ ...f, title: e.target.value })}
          placeholder={mode === 'cost' ? 'e.g. Claude Max' : f.kind === 'credit' ? 'e.g. October outage credit' : 'e.g. ASP onboarding fee'}
          inputProps={{ maxLength: 200 }} />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <AppTextField label={mode === 'cost' ? 'Vendor' : 'Paid by (optional)'} value={f.vendor ?? ''}
            onChange={e => setF({ ...f, vendor: e.target.value || null })}
            placeholder={mode === 'cost' ? 'e.g. Anthropic' : 'e.g. the customer’s billing contact'} inputProps={{ maxLength: 200 }} />
          <SuggestField label="Category" required maxLength={60} value={f.category} suggestions={mode === 'cost' ? CATEGORIES : INCOME_CATEGORIES}
            onChange={v => setF({ ...f, category: v })} />
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'start' }}>
          <MoneyField label={f.cadence === 'annual_spread' ? 'Amount per year' : 'Amount'} required value={amount} onChange={setAmount}
            error={amount !== '' && !moneyOk(amt)}
            helperText={f.cadence === 'annual_spread' && moneyOk(amt) ? `${money(amt / 12)} counted each month for 12 months` : ' '} />
          <Box sx={{ pt: 0.5 }}>
            <ChoiceToggle ariaLabel="How often" value={f.cadence} options={CADENCES} onChange={v => setF({ ...f, cadence: v })} />
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          <SelectField label={f.cadence === 'one_off' ? 'Month' : 'Starts'} value={f.start_month} options={asOptions(months)}
            onChange={v => setF({ ...f, start_month: v })} />
          {f.cadence !== 'one_off' && (
            <SelectField label="Last month" value={f.end_month ?? ''} error={!endOk}
              helperText={!endOk ? 'Must be on or after the start month' : f.end_month ? ' ' : 'Keeps going every month until you cut it off'}
              options={[{ value: '', label: 'Continues (no end)' }, ...asOptions(months.filter(m => m >= f.start_month))]}
              onChange={v => setF({ ...f, end_month: v || null })} />
          )}
        </Box>
        <AppTextField label="Reference" value={f.reference ?? ''} onChange={e => setF({ ...f, reference: e.target.value || null })}
          placeholder="Invoice # or issue number / link" inputProps={{ maxLength: 400 }}
          helperText="Numbers or links only — never patient details." />
        <AppTextField label="Notes" value={f.notes ?? ''} onChange={e => setF({ ...f, notes: e.target.value || null })}
          multiline minRows={2} inputProps={{ maxLength: 400 }} />
      </Box>
    </AppDialog>
  )
}

function blank(month: string, mode: LineMode): CostItemInput {
  return {
    title: '', vendor: null, category: '', amount_usd: 0, cadence: mode === 'cost' ? 'monthly' : 'one_off', start_month: month, end_month: null,
    reference: null, notes: null, kind: mode === 'cost' ? 'cost' : 'income', pharmacy_id: null,
  }
}

// ── change one month ───────────────────────────────────────────────────────────────────────────────

export function MonthAmountDialog({ open, title, month, current, busy, error, onSave, onClose }: {
  open: boolean; title: string; month: string; current: number; busy: boolean; error: string | null
  onSave: (amount: number, reason: string | null) => void; onClose: () => void
}) {
  const [amount, setAmount] = useState(''); const [reason, setReason] = useState('')
  useEffect(() => { if (open) { setAmount(String(current)); setReason('') } }, [open, current])
  const amt = parseMoney(amount)
  return (
    <AppDialog open={open} onClose={onClose} busy={busy} maxWidth="xs" title={`${title} — ${monthLabel(month)} only`}
      actions={<>
        <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
        <AppButton variant="contained" disabled={!moneyOk(amt) || busy} onClick={() => onSave(Math.round(amt * 100) / 100, reason.trim() || null)}>Save</AppButton>
      </>}>
      <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
        {error && <Notice severity="error">{error}</Notice>}
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>Other months keep the normal amount.</Box>
        <MoneyField label={`Amount for ${monthLabel(month)}`} value={amount} onChange={setAmount} />
        <AppTextField label="Why (optional)" value={reason} onChange={e => setReason(e.target.value)} inputProps={{ maxLength: 400 }} />
      </Box>
    </AppDialog>
  )
}

// ── cut off ────────────────────────────────────────────────────────────────────────────────────────

export function CutOffDialog({ open, title, startMonth, defaultMonth, busy, error, onSave, onClose }: {
  open: boolean; title: string; startMonth: string; defaultMonth: string; busy: boolean; error: string | null
  onSave: (endMonth: string) => void; onClose: () => void
}) {
  const months = useMemo(() => monthOptions().filter(m => m >= startMonth), [startMonth])
  const [end, setEnd] = useState(defaultMonth)
  useEffect(() => { if (open) setEnd(defaultMonth >= startMonth ? defaultMonth : startMonth) }, [open, defaultMonth, startMonth])
  return (
    <AppDialog open={open} onClose={onClose} busy={busy} maxWidth="xs" title={`Cut off ${title}`}
      actions={<>
        <AppButton onClick={onClose} disabled={busy}>Cancel</AppButton>
        <AppButton variant="contained" color="warning" disabled={busy} onClick={() => onSave(end)}>Cut off</AppButton>
      </>}>
      <Box sx={{ display: 'grid', gap: 2, pt: 0.5 }}>
        {error && <Notice severity="error">{error}</Notice>}
        <SelectField label="Last month it counts" value={end} options={asOptions(months)} onChange={setEnd} />
        <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
          It stops after {monthLabel(end)}. Earlier months keep it — nothing is deleted. You can resume it later.
        </Box>
      </Box>
    </AppDialog>
  )
}
